import { motion } from "motion/react";
import { Mail, MapPin, Phone } from "lucide-react";

export default function Contact() {
  const contactDetails = {
    company: "Wesitex Sdn Bhd (1528285-V)",
    address: "Level 1, Menara Amplewest, 6, Jalan P. Ramlee, Kuala Lumpur, 50250 Kuala Lumpur, Wilayah Persekutuan Kuala Lumpur.",
    phone: "+60 11 6509 5910",
    email: "wesitex.team@gmail.com",
    mapQuery: "Menara+Amplewest",
    whatsappUrl: "https://wa.me/601165095910?text=Hi%20Wesitex%2C%20I'm%20interested%20to%20know%20more%20about%20your%20services.",
    emailUrl: "mailto:wesitex.team@gmail.com",
    mapUrl: "https://www.google.com/maps/search/?api=1&query=Level+1,+Menara+Amplewest,+6,+Jalan+P.+Ramlee,+Kuala+Lumpur,+50250",
  };

  return (
    <section id="contact" className="relative py-16 sm:py-28 bg-transparent">
      {/* Premium Glowing Purple Orb - GPU Accelerated to prevent initial scroll lag */}
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[350px] h-[350px] bg-brand-purple/10 blur-[130px] rounded-full pointer-events-none transform-gpu translate-z-0 will-change-transform" />
      <div className="absolute bottom-10 right-10 w-[250px] h-[250px] bg-brand-purple/5 blur-[100px] rounded-full pointer-events-none transform-gpu translate-z-0 will-change-transform" />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Section Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-10 sm:mb-16"
        >
          <h2 className="text-2xl sm:text-6xl font-extrabold tracking-widest text-white uppercase">
            Contact <span className="text-brand-purple">Us</span>
          </h2>
          <div className="mt-2 sm:mt-4 mx-auto h-1 w-16 sm:w-24 bg-gradient-to-r from-brand-purple to-brand-electric rounded-full" />
        </motion.div>

        {/* Content Layout */}
        <div className="grid gap-4 xs:gap-8 sm:gap-12 grid-cols-2 items-center">
          {/* Left Column: Google Map */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
            className="w-full h-[140px] xs:h-[220px] sm:h-[320px] md:h-[420px] rounded-2xl sm:rounded-3xl border border-white/10 overflow-hidden shadow-[0_0_30px_rgba(139,92,246,0.15)] bg-[#0c0a1e]/40 backdrop-blur-md relative group"
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3983.7909380963507!2d101.70366157588147!3d3.1520682968236166!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc482aa5490f5d%3A0x84a79fa33715ef73!2sMenara%20Ample%20West!5e0!3m2!1sen!2smy!4v1716039500000!5m2!1sen!2smy"
              width="100%"
              height="100%"
              style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) brightness(95%) contrast(90%)" }}
              allowFullScreen={true}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Wesitex Office Location"
              className="transition-transform duration-700 group-hover:scale-[1.02]"
            />
          </motion.div>

          {/* Right Column: Company Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
            className="flex flex-col space-y-3 sm:space-y-8 pl-0 sm:pl-6 text-left"
          >
            <div>
              <motion.h3
                className="text-xs xs:text-sm sm:text-2xl md:text-3xl font-extrabold text-white tracking-tight leading-normal sm:leading-none drop-shadow-[0_0_15px_rgba(255,255,255,0.1)]"
              >
                {contactDetails.company}
              </motion.h3>
              <div className="mt-1 sm:mt-3 h-[2px] w-8 sm:w-16 bg-brand-purple/70" />
            </div>

            <div className="space-y-2 sm:space-y-6">
              {/* Address */}
              <motion.a
                href={contactDetails.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ x: 6 }}
                className="flex items-start gap-1.5 xs:gap-4 group cursor-pointer"
              >
                <div className="flex h-5 w-5 xs:h-8 xs:w-8 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-lg sm:rounded-2xl border border-white/10 bg-white/5 text-brand-purple transition-all duration-300 group-hover:bg-brand-purple group-hover:text-white group-hover:shadow-[0_0_15px_rgba(139,92,246,0.4)]">
                  <MapPin className="h-2.5 w-2.5 xs:h-4 xs:w-4 sm:h-5 sm:w-5" />
                </div>
                <div className="flex flex-col space-y-0.5">
                  <span className="text-[8px] xs:text-[10px] sm:text-xs font-semibold text-white/50 uppercase tracking-widest">Office Address</span>
                  <span className="text-[10px] xs:text-xs sm:text-base font-medium text-white/90 leading-normal sm:leading-relaxed group-hover:text-white transition-colors duration-200">
                    {contactDetails.address}
                  </span>
                </div>
              </motion.a>

              {/* Phone */}
              <motion.a
                href={contactDetails.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ x: 6 }}
                className="flex items-start gap-1.5 xs:gap-4 group cursor-pointer"
              >
                <div className="flex h-5 w-5 xs:h-8 xs:w-8 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-lg sm:rounded-2xl border border-white/10 bg-white/5 text-brand-purple transition-all duration-300 group-hover:bg-brand-purple group-hover:text-white group-hover:shadow-[0_0_15px_rgba(139,92,246,0.4)]">
                  <Phone className="h-2.5 w-2.5 xs:h-4 xs:w-4 sm:h-5 sm:w-5" />
                </div>
                <div className="flex flex-col space-y-0.5">
                  <span className="text-[8px] xs:text-[10px] sm:text-xs font-semibold text-white/50 uppercase tracking-widest">Phone Number</span>
                  <span className="text-[10px] xs:text-xs sm:text-base font-medium text-white/90 group-hover:text-white transition-colors duration-200">
                    {contactDetails.phone}
                  </span>
                </div>
              </motion.a>

              {/* Email */}
              <motion.a
                href={contactDetails.emailUrl}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ x: 6 }}
                className="flex items-start gap-1.5 xs:gap-4 group cursor-pointer"
              >
                <div className="flex h-5 w-5 xs:h-8 xs:w-8 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-lg sm:rounded-2xl border border-white/10 bg-white/5 text-brand-purple transition-all duration-300 group-hover:bg-brand-purple group-hover:text-white group-hover:shadow-[0_0_15px_rgba(139,92,246,0.4)]">
                  <Mail className="h-2.5 w-2.5 xs:h-4 xs:w-4 sm:h-5 sm:w-5" />
                </div>
                <div className="flex flex-col space-y-0.5">
                  <span className="text-[8px] xs:text-[10px] sm:text-xs font-semibold text-white/50 uppercase tracking-widest">Email Address</span>
                  <span className="text-[10px] xs:text-xs sm:text-base font-medium text-white/90 group-hover:text-white transition-colors duration-200">
                    {contactDetails.email}
                  </span>
                </div>
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
